import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Drawer, IconButton, Typography, CircularProgress } from "@mui/material";
import { makeStyles } from "@Compat/makeStyles";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons/faXmark';
import { renderLegalMarkdown } from "@Components/legal/renderLegalMarkdown";
import { useLegalDrawer } from "../LegalDrawerContext";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 760,
    maxWidth: "100vw",
    height: "100vh",
    background: theme.palette.background.default,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      width: "100vw"
    }
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    zIndex: 2
  },
  title: {
    fontWeight: 700,
    fontSize: "1.05rem"
  },
  body: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(3, 3.5),
    background: theme.palette.background.paper,
    // Comfortable reading measure for the long legal copy.
    "& h1, & h2, & h3, & h4": {
      lineHeight: 1.25,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1)
    },
    "& p, & li": {
      lineHeight: 1.6,
      fontSize: "0.95rem"
    },
    "& a": {
      color: theme.palette.primary.main
    },
    "& pre": {
      whiteSpace: "pre-wrap",
      background: theme.palette.action.hover,
      padding: theme.spacing(1.5),
      borderRadius: 6,
      overflowX: "auto"
    }
  },
  loadingOverlay: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    background: theme.palette.background.paper
  }
}));

const LegalDrawer: React.FC = () => {
  const classes = useStyles();
  const { isOpen, url, title, close } = useLegalDrawer();
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");

  const load = useCallback(async (docUrl: string) => {
    setLoading(true);
    setText("");
    try {
      let res = await fetch(docUrl, { cache: "no-store" });
      if (!res.ok) throw new Error("not ok");
      setText(await res.text());
    } catch {
      // Fall back to the English agreement if the locale-specific file
      // is missing (fr/de/it currently reuse the EN document).
      try {
        const res2 = await fetch("/legal/subscription-license-agreement.en.md", { cache: "no-store" });
        setText(await res2.text());
      } catch {
        setText("");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && url) {
      load(url);
    }
  }, [isOpen, url, load]);

  const html = useMemo(() => renderLegalMarkdown(text), [text]);

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={close}
      ModalProps={{ keepMounted: false }}
      PaperProps={{ className: classes.paper }}
    >
      <div className={classes.header}>
        <Typography component="span" className={classes.title}>
          {title}
        </Typography>
        <IconButton size="small" onClick={close} aria-label="Close">
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 20 }} />
        </IconButton>
      </div>
      {loading ? (
        <div className={classes.loadingOverlay}>
          <CircularProgress size={32} />
        </div>
      ) : (
        <Box className={classes.body} dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </Drawer>
  );
};

export default LegalDrawer;
